import type { Box } from "../coreComponents/Box";
import type { EventHandlerCallback } from "../coreComponents/types";
import { getInstances } from "../renderingEngine/renderer";
import { eventToCallbackMap } from "./interactionObserverConstants";
import type { CallbackPayloadInternal } from "./interactionTypes";

/**
 * Trigger the event on the ID with the payload provided.
 * @param id
 * @param event
 * @param payload
 * @returns
 */
export const triggerEvent = (
  id: string,
  event: string,
  payload: Record<string, unknown> // We're enforcing the payload to be an object
) => {
  const instances = getInstances();
  const component = instances[id] as Box;
  const callbackProp =
    eventToCallbackMap[event as keyof typeof eventToCallbackMap];
  if (!component) {
    // Reachable only if we try to trigger an event on a component that doesn't exist
    // anymore.
    throw Error(`Component (id: ${id}) not found, while firing event ${event}`);
  }
  const callback: EventHandlerCallback | null =
    // @ts-expect-error // https://thevetted.atlassian.net/browse/DS-4767
    component.props.eventHandlers?.[callbackProp] ?? null;

  // Let's check if there are any callbacks registered for the event.
  if (!callback) {
    return {
      handled: false,
      propagationStopped: false
    };
  }
  // This has to be `any` because collector can store any arbitrary value
  // that is returned by the callback.
  // Not sure if this is the best way to do it. But since the entire object is
  // returned, it will be easier to change it when we find a better way to do it.

  let hasPropagationStopped = false;

  const payloadWithEventData: CallbackPayloadInternal = {
    ...payload,
    __internal: {
      stopPropagation: false,
      stop() {
        this.stopPropagation = true;
      },
      event,
      id
    }
  };
  // Callbacks can choose to stop the propagation of the event
  // by calling the stop method on the payload or by setting
  // the stopPropagation to true.
  // @ts-expect-error // https://thevetted.atlassian.net/browse/DS-4767
  const isHandled = callback(payloadWithEventData);
  // If we don't return anything then we consider it as handled.
  // But we can return `false` if we want to explicitly say that the event
  // not handled by the callback.
  if (payloadWithEventData.__internal.stopPropagation) {
    hasPropagationStopped = true;
  }

  return {
    handled: isHandled === false ? false : true,
    propagationStopped: hasPropagationStopped
  };
};
